// frontend/src/components/ProjectList.js
// Componente para mostrar una tabla de proyectos.
// ACTUALIZADO: Añadida la columna "Tipo" para diferenciar iniciativas.
// CORRECCIÓN: Resaltado visual de filas más prominente Y SELECTIVO cuando hay filtros de búsqueda aplicados.
// CORRECCIÓN: Contenido de la columna 'Proyecto' condicional para iniciativas MAP.

import React, { useState } from 'react';
import { useProjectStore } from '../store/projectStore';
import LoadingSpinner from './LoadingSpinner';
import CustomConfirmDialog from './CustomConfirmDialog';
import { PencilIcon, TrashIcon, EyeIcon } from '@heroicons/react/24/solid';

const formatCurrency = (value) => new Intl.NumberFormat('es-CL', {
  style: 'currency',
  currency: 'USD',
  maximumFractionDigits: 0,
}).format(Number(value || 0));

function ProjectList({ projects, isLoading, onEditProject, onViewProjectDetails, activeFilters = {} }) {
  const { deleteProject } = useProjectStore();

  const [showConfirm, setShowConfirm] = useState(false);
  const [projectIdToDelete, setProjectIdToDelete] = useState(null);
  const [selectedProjectId, setSelectedProjectId] = useState(null);

  const confirmDelete = (projectId) => {
    setProjectIdToDelete(projectId);
    setShowConfirm(true);
  };

  const handleDeleteConfirmed = async () => {
    if (projectIdToDelete) {
      await deleteProject(projectIdToDelete);
      setProjectIdToDelete(null);
      setSelectedProjectId(null);
    }
    setShowConfirm(false);
  };

  const handleDeleteCancelled = () => {
    setProjectIdToDelete(null);
    setShowConfirm(false);
  };

  const getTypeColor = (typeName) => {
    switch (typeName) {
      case 'MAP':
        return 'bg-blue-100 text-blue-800';
      case 'PoC':
        return 'bg-purple-100 text-purple-800';
      case 'Proyecto de Implementación':
        return 'bg-amber-100 text-amber-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getStatusLabel = (status) => {
    const labels = {
      active: 'Activo',
      closed: 'Cerrado',
      pending: 'Pendiente',
      cancelled: 'Cancelado',
    };

    return labels[status] || status || 'N/A';
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'active':
        return 'bg-emerald-100 text-emerald-800';
      case 'closed':
        return 'bg-slate-100 text-slate-700';
      case 'cancelled':
        return 'bg-rose-100 text-rose-800';
      default:
        return 'bg-amber-100 text-amber-800';
    }
  };

  const toggleSelectedProject = (projectId) => {
    setSelectedProjectId((currentProjectId) => currentProjectId === projectId ? null : projectId);
  };

  const handleRowKeyDown = (event, projectId) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      toggleSelectedProject(projectId);
    }
  };

  // Función para determinar si una fila de proyecto debe ser resaltada
  const shouldHighlight = (project) => {
    const { projectName, opportunityNumber } = activeFilters;

    // Convertir a minúsculas para comparación insensible a mayúsculas/minúsculas
    const lowerCaseProjectNameFilter = projectName?.toLowerCase();
    const lowerCaseOpportunityNumberFilter = opportunityNumber?.toLowerCase();

    // Resaltar si hay un filtro de nombre de proyecto y el nombre del proyecto coincide
    if (lowerCaseProjectNameFilter && lowerCaseProjectNameFilter !== '' &&
        project.projectName?.toLowerCase().includes(lowerCaseProjectNameFilter)) {
      return true;
    }

    // Resaltar si hay un filtro de número de oportunidad y el número de oportunidad coincide
    if (lowerCaseOpportunityNumberFilter && lowerCaseOpportunityNumberFilter !== '' &&
        project.client?.opportunityNumber?.toLowerCase().includes(lowerCaseOpportunityNumberFilter)) {
      return true;
    }

    return false; // No resaltar por defecto
  };


  if (isLoading) {
    return (
      <div className="flex justify-center items-center h-48">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  if (!projects || projects.length === 0) {
    return (
      <div className="text-center py-10 px-4 bg-gray-50 rounded-lg border-2 border-dashed border-gray-300">
        <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
          <path vectorEffect="non-scaling-stroke" strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 13h6m-3-3v6m-9 1V7a2 2 0 012-2h12a2 2 0 012 2v10a2 2 0 01-2 2H4a2 2 0 01-2-2z" />
        </svg>
        <h3 className="mt-2 text-sm font-medium text-gray-900">No se encontraron iniciativas</h3>
        <p className="mt-1 text-sm text-gray-500">Intenta ajustar los filtros de búsqueda o añade una nueva iniciativa.</p>
      </div>
    );
  }

  return (
    <div className="overflow-hidden rounded-lg border border-gray-200 bg-white shadow-sm">
      <div className="hidden grid-cols-[1.7fr_0.7fr_1.4fr_1.5fr_1fr_1fr] gap-4 border-b border-gray-200 bg-gray-50 px-4 py-3 text-xs font-medium uppercase tracking-wider text-gray-500 lg:grid">
        <span>Cliente / Oportunidad</span>
        <span>Tipo</span>
        <span>Proyecto</span>
        <span>Responsables</span>
        <span>Monto</span>
        <span>Estado</span>
      </div>
      <div className="divide-y divide-gray-200">
        {projects.map((project) => {
          const highlighted = shouldHighlight(project);
          const isSelected = selectedProjectId === project.id;

          return (
            <div key={project.id} className={highlighted ? 'bg-yellow-50' : 'bg-white'}>
              <button
                type="button"
                onClick={() => toggleSelectedProject(project.id)}
                onKeyDown={(event) => handleRowKeyDown(event, project.id)}
                aria-expanded={isSelected}
                className={`block w-full px-4 py-4 text-left transition-colors hover:bg-slate-50 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-blue-500 ${isSelected ? 'bg-blue-50/70' : ''}`}
              >
                <div className="grid grid-cols-1 gap-3 lg:grid-cols-[1.7fr_0.7fr_1.4fr_1.5fr_1fr_1fr] lg:items-center lg:gap-4">
                  <div className="min-w-0">
                    <p className="truncate text-sm font-semibold text-gray-900">{project.client?.organizationName || 'N/A'}</p>
                    <p className="mt-1 truncate text-xs text-gray-500">{project.client?.opportunityNumber || 'Sin oportunidad'}</p>
                  </div>
                  <div>
                    <span className={`inline-flex rounded-full px-2 py-0.5 text-xs font-semibold ${getTypeColor(project.initiativeType?.name)}`}>
                      {project.initiativeType?.name || 'N/A'}
                    </span>
                  </div>
                  <div className="min-w-0">
                    <p className="truncate text-sm text-gray-800">{project.initiativeType?.name === 'MAP' ? 'AWS-MAP' : (project.projectName || 'N/A')}</p>
                  </div>
                  <div className="min-w-0">
                    <p className="truncate text-sm text-gray-900">{project.client?.accountManager || 'N/A'} <span className="text-xs text-gray-500">(AM)</span></p>
                    <p className="mt-1 truncate text-xs text-gray-500">{project.client?.solutionSales || 'N/A'} (SS)</p>
                  </div>
                  <div>
                    <p className="text-sm font-bold text-gray-900">{formatCurrency(project.totalExpectedAmount)}</p>
                  </div>
                  <div className="flex flex-wrap items-center gap-3">
                    <span className={`inline-flex rounded-full px-2 py-0.5 text-xs font-semibold ${getStatusColor(project.status)}`}>
                      {getStatusLabel(project.status)}
                    </span>
                    <div className="flex min-w-[120px] flex-1 items-center lg:basis-full">
                      <div className="h-2.5 flex-1 rounded-full bg-gray-200">
                        <div className="h-2.5 rounded-full bg-blue-600" style={{ width: `${project.overallProgress || 0}%` }} />
                      </div>
                      <span className="ml-2 text-xs font-medium text-gray-700">{project.overallProgress || 0}%</span>
                    </div>
                  </div>
                </div>
              </button>
              {isSelected && (
                <div className="border-t border-blue-100 bg-blue-50/80 px-4 py-3">
                  <div className="flex flex-col gap-3 rounded-md border border-blue-100 bg-white px-4 py-3 shadow-sm sm:flex-row sm:items-center sm:justify-between">
                    <div>
                      <p className="text-sm font-semibold text-slate-900">Acciones para esta iniciativa</p>
                      <p className="text-xs text-slate-500">{project.client?.organizationName || 'Cliente'} · {project.initiativeType?.name || 'Iniciativa'}</p>
                    </div>
                    <div className="flex flex-wrap gap-2">
                      <button type="button" onClick={() => onViewProjectDetails(project)} className="inline-flex items-center gap-2 rounded-md bg-blue-600 px-3 py-2 text-sm font-medium text-white shadow-sm transition-colors hover:bg-blue-700">
                        <EyeIcon className="h-4 w-4" />
                        Ver detalles
                      </button>
                      <button type="button" onClick={() => onEditProject(project)} className="inline-flex items-center gap-2 rounded-md border border-indigo-200 bg-white px-3 py-2 text-sm font-medium text-indigo-700 shadow-sm transition-colors hover:bg-indigo-50">
                        <PencilIcon className="h-4 w-4" />
                        Editar
                      </button>
                      <button type="button" onClick={() => confirmDelete(project.id)} className="inline-flex items-center gap-2 rounded-md border border-red-200 bg-white px-3 py-2 text-sm font-medium text-red-700 shadow-sm transition-colors hover:bg-red-50">
                        <TrashIcon className="h-4 w-4" />
                        Eliminar
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>
      <CustomConfirmDialog
        show={showConfirm}
        message="¿Estás seguro de que quieres eliminar esta iniciativa? Esta acción eliminará todas las fases y actividades asociadas y no se puede deshacer."
        onConfirm={handleDeleteConfirmed}
        onCancel={handleDeleteCancelled}
      />
    </div>
  );
}

export default ProjectList;